import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Typography,
    Box,
    IconButton,
    Tooltip,
    alpha,
    useTheme
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { formatToUIDateTimeLong } from '../../utils/dateUtils';
import { CareRequestDTO, CarePriority, CareStatus } from '../../types/crm.types';

interface CareRequestListProps {
    tickets: CareRequestDTO[];
    onView: (ticket: CareRequestDTO) => void;
    onEdit: (ticket: CareRequestDTO) => void;
    userRole?: string;
}

const CareRequestList: React.FC<CareRequestListProps> = ({ tickets, onView, onEdit, userRole }) => {
    const theme = useTheme();
    const canEdit = userRole === 'ADMIN' || userRole === 'FACILITY_ADMIN' || userRole === 'SUPER_ADMIN' || userRole === 'CLIENT_ADMIN';

    const getPriorityStyle = (priority: CarePriority) => {
        switch (priority) {
            case CarePriority.URGENT: return { bgcolor: '#fee2e2', color: '#dc2626' }; // soft red
            case CarePriority.HIGH: return { bgcolor: '#ffedd5', color: '#ea580c' }; // soft orange
            case CarePriority.MEDIUM: return { bgcolor: '#e0f2fe', color: '#0284c7' }; // soft blue
            case CarePriority.LOW: return { bgcolor: '#dcfce7', color: '#16a34a' }; // soft green
            default: return { bgcolor: '#f3f4f6', color: '#4b5563' }; // soft grey
        }
    };

    const getStatusStyle = (status: CareStatus) => {
        switch (status) {
            case CareStatus.NEW: return { bgcolor: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd' };
            case CareStatus.PENDING: return { bgcolor: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d' };
            case CareStatus.ASSIGNED: return { bgcolor: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc' };
            case CareStatus.IN_PROGRESS: return { bgcolor: '#ffedd5', color: '#c2410c', border: '1px solid #fdba74' };
            case CareStatus.HOLD: return { bgcolor: '#ede9fe', color: '#6d28d9', border: '1px solid #c4b5fd' };
            case CareStatus.RESOLVED: return { bgcolor: '#dcfce7', color: '#15803d', border: '1px solid #86efac' };
            case CareStatus.CLOSED: return { bgcolor: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' };
            default: return { bgcolor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' };
        }
    };

    const capitalize = (str: string) => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
    const capitalizeAll = (str: string) => str ? str.split(' ').map(s => capitalize(s)).join(' ') : '';

    return (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table sx={{ minWidth: 750, '& .MuiTableCell-root': { whiteSpace: 'nowrap', verticalAlign: 'middle' } }} aria-label="care request table">
                <TableHead sx={{
                    "& .MuiTableCell-head": {
                        color: "#434445ff !important",
                    }
                }}>
                    <TableRow>
                        <TableCell>Request Id</TableCell>
                        <TableCell>Resident Name</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Resident Need</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Response Deadline</TableCell>
                        <TableCell align="left">Raised On</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tickets.map((ticket) => (
                        <TableRow
                            key={ticket.id}
                            hover
                            onClick={() => onView(ticket)}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer' }}
                        >
                            <TableCell>
                                {ticket.requestNumber}
                            </TableCell>
                            <TableCell>
                                {capitalizeAll(ticket.residentName || '')}
                            </TableCell>
                            <TableCell>
                                <Chip
                                    label={capitalize(ticket.priority)}
                                    size="small"
                                    sx={{
                                        fontWeight: '800',
                                        borderRadius: '6px',
                                        height: 24,
                                        px: 0.5,
                                        fontSize: '0.7rem',
                                        ...getPriorityStyle(ticket.priority)
                                    }}
                                />
                            </TableCell>
                            <TableCell>
                                {capitalize(ticket.category)}
                            </TableCell>
                            <TableCell>
                                {ticket.subcategory}
                            </TableCell>
                            <TableCell>
                                <Chip
                                    label={capitalizeAll(ticket.status.replace(/_/g, ' '))}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        borderRadius: '16px',
                                        height: 26,
                                        px: 0.5,
                                        fontSize: '0.72rem',
                                        ...getStatusStyle(ticket.status),
                                    }}
                                />
                            </TableCell>
                            <TableCell>
                                {new Date(ticket.slaDeadline).toLocaleDateString('en-GB')}
                                {ticket.isEscalated && (
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                            label="ESCALATED"
                                            variant="filled"
                                            size="small"
                                            color="error"
                                            sx={{ height: 16, fontSize: '0.55rem', fontWeight: '900', borderRadius: 1 }}
                                        />
                                    </Box>
                                )}
                            </TableCell>
                            <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                                {formatToUIDateTimeLong(ticket.createdAt)}
                            </TableCell>
                            <TableCell align="right">
                                <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                                    <Tooltip title="View Details">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); onView(ticket); }}
                                            sx={{
                                                bgcolor: 'rgba(72, 202, 228, 0.12)',
                                                color: '#0e86a0',
                                                '&:hover': {
                                                    bgcolor: '#48CAE4',
                                                    color: '#000000',
                                                    transform: 'translateY(-2px)'
                                                },
                                                transition: 'all 0.2s',
                                                borderRadius: '12px',
                                                p: 1
                                            }}
                                        >
                                            <VisibilityIcon sx={{ fontSize: '1.2rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                    {canEdit && (
                                        <Tooltip title={ticket.status === CareStatus.CLOSED ? "Cannot edit closed request" : "Edit Request"}>
                                            <span
                                                onClick={(e) => { if (ticket.status === CareStatus.CLOSED) e.stopPropagation(); }}
                                                style={{ pointerEvents: ticket.status === CareStatus.CLOSED ? 'none' : 'auto', display: 'inline-flex' }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); if (ticket.status !== CareStatus.CLOSED) onEdit(ticket); }}
                                                    disabled={ticket.status === CareStatus.CLOSED}
                                                    sx={{
                                                        bgcolor: 'rgba(72, 202, 228, 0.12)',
                                                        color: '#0e86a0',
                                                        '&:hover': {
                                                            bgcolor: '#48CAE4',
                                                            color: '#000000',
                                                            transform: 'translateY(-2px)'
                                                        },
                                                        transition: 'all 0.2s',
                                                        borderRadius: '12px',
                                                        p: 1
                                                    }}
                                                >
                                                    <EditIcon sx={{ fontSize: '1.2rem' }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CareRequestList;
